import { type RouteRecordRaw } from 'vue-router'
import { PERMISSION_READ_ASSETS } from '@/vue/helpers/permissions'
import { check } from '../guards/permission'
import type { PortfolioWithAssetId, PortfolioWithAssetIdAndYear } from './types'

export interface RouteParams {
    'asset.roadmap': PortfolioWithAssetIdAndYear
    'asset.roadmap-data-collection': PortfolioWithAssetIdAndYear

    'asset.meters-overview': PortfolioWithAssetIdAndYear
    'asset.analytics': PortfolioWithAssetIdAndYear
    'asset.overview': PortfolioWithAssetIdAndYear
    'asset.details': PortfolioWithAssetIdAndYear
    'asset.metrics': PortfolioWithAssetIdAndYear
    'asset.meter-analytics': PortfolioWithAssetIdAndYear
    'asset.scores': PortfolioWithAssetIdAndYear
    'asset.building-units': PortfolioWithAssetIdAndYear

    // Input Portal routes
    'asset.input-portal.overview': PortfolioWithAssetIdAndYear
    'asset.input-portal.asset-characteristics': PortfolioWithAssetIdAndYear
    'asset.input-portal.roadmap': PortfolioWithAssetIdAndYear
    'asset.input-portal.reporting-data': PortfolioWithAssetIdAndYear
    'asset.input-portal.certifications': PortfolioWithAssetIdAndYear
    'asset.input-portal.meters-and-consumptions': PortfolioWithAssetIdAndYear
    'asset.input-portal.assessments-and-measures': PortfolioWithAssetIdAndYear
    'asset.input-portal.building-units': PortfolioWithAssetIdAndYear
    'asset.input-portal.physical-climate': PortfolioWithAssetIdAndYear
    'asset.input-portal.social-data': PortfolioWithAssetIdAndYear
    'asset.input-portal.financial-data': PortfolioWithAssetIdAndYear
    'asset.input-portal.document-library': PortfolioWithAssetIdAndYear
    'asset.input-portal.development': PortfolioWithAssetIdAndYear
    'asset.input-portal.automations': PortfolioWithAssetId
    'asset.input-portal.custom-fields': PortfolioWithAssetIdAndYear
    'asset.input-portal.audit-log': PortfolioWithAssetIdAndYear
    'asset.audit-log': PortfolioWithAssetIdAndYear
}

/**
 * Asset routes for analytics mode
 * All components are lazy-loaded for proper code splitting
 */
export const assetAnalyticsRoutes: RouteRecordRaw[] = [
    {
        path: 'portfolio/:portfolio_id/asset/:asset_id/:reporting_year',
        component: () => import('@/vue/pages/analytics/asset/Layout.vue'),
        beforeEnter: check([PERMISSION_READ_ASSETS], 'dashboard'),
        children: [
            {
                path: '',
                redirect: { name: 'asset.overview' },
            },
            {
                path: 'overview',
                name: 'asset.overview',
                component: () => import('@/vue/pages/asset/AssetOverviewPage.vue'),
                meta: {
                    title: 'general.overview',
                    fluid: true,
                    redirectsTo: 'asset.input-portal.overview',
                },
            },
            {
                path: 'details',
                name: 'asset.details',
                component: () => import('@/vue/pages/asset/AssetDetailsPage.vue'),
                meta: {
                    title: 'general.asset-details',
                    fluid: true,
                    redirectsTo: 'asset.input-portal.overview',
                },
            },
            {
                path: 'esg-data',
                name: 'asset.analytics',
                component: () => import('@/vue/pages/asset/AssetAnalyticsPage.vue'),
                meta: {
                    title: 'general.sub-navigation.performance',
                    fluid: true,
                    redirectsTo: 'asset.input-portal.overview',
                },
                beforeEnter: check([PERMISSION_READ_ASSETS], 'asset.overview', 'asset-esg-data-page'),
            },
            {
                path: 'meter-analytics',
                name: 'asset.meter-analytics',
                component: () => import('@/vue/pages/meters/AssetMeterAnalyticsPage.vue'),
                meta: {
                    title: 'general.meter-analytics',
                    fluid: true,
                    redirectsTo: 'asset.input-portal.meters-and-consumptions',
                    feature: {
                        flag: 'asset-meter-analytics',
                        fallbackRoute: 'asset.metrics',
                    },
                },
            },
            {
                path: 'metrics',
                name: 'asset.metrics',
                component: () => import('@/vue/pages/metrics/AssetMetricsPage.vue'),
                meta: {
                    title: 'general.metrics',
                    fluid: true,
                    redirectsTo: 'asset.input-portal.overview',
                },
            },
            {
                path: 'scores',
                name: 'asset.scores',
                component: () => import('@/vue/pages/gresb/AssetScoresPage.vue'),
                meta: {
                    title: 'general.scores',
                    fluid: true,
                    redirectsTo: 'asset.input-portal.overview',
                },
                beforeEnter: check([PERMISSION_READ_ASSETS], 'asset.overview', 'asset-scores-page'),
            },
            {
                path: 'roadmap',
                name: 'asset.roadmap',
                component: () => import('@/vue/pages/roadmap/asset/AssetRoadmapPage.vue'),
                meta: {
                    title: 'general.sub-navigation.roadmap-analysis',
                    fluid: true,
                    redirectsTo: 'asset.input-portal.roadmap',
                },
                beforeEnter: check([PERMISSION_READ_ASSETS], 'asset.overview', 'asset-roadmap-page'),
            },
            {
                path: 'building-units',
                name: 'asset.building-units',
                component: () => import('@/vue/pages/asset/building-units/AssetBuildingUnitsPage.vue'),
                meta: {
                    title: 'Building Units',
                    fluid: true,
                    redirectsTo: 'asset.input-portal.building-units',
                    feature: {
                        flag: 'building-units-analytics-page',
                        fallbackRoute: 'asset.overview',
                    },
                },
            },
            {
                path: 'audit-log',
                name: 'asset.audit-log',
                component: () => import('@/vue/pages/asset/AuditLogPage.vue'),
                meta: {
                    title: 'audit-log.title',
                    fluid: true,
                    redirectsTo: 'asset.input-portal.audit-log',
                    feature: {
                        flag: 'new-audit-system',
                        fallbackRoute: 'asset.overview',
                    },
                },
            },
        ],
    },
]

/**
 * Asset routes for collection mode (Input Portal)
 * Each page is now a proper nested route with lazy loading
 */
export const assetCollectionRoutes: RouteRecordRaw[] = [
    {
        path: 'portfolio/:portfolio_id/asset/:asset_id/:reporting_year',
        component: () => import('@/vue/pages/collection/asset/Layout.vue'),
        beforeEnter: check([PERMISSION_READ_ASSETS], 'dashboard'),
        children: [
            {
                path: '',
                redirect: { name: 'asset.input-portal.overview' },
            },
            {
                path: 'overview',
                name: 'asset.input-portal.overview',
                component: () => import('@/vue/pages/asset/input-portal-pages/OverviewPage.vue'),
                meta: {
                    title: 'general.overview',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.overview',
                },
            },
            {
                path: 'asset-characteristics',
                name: 'asset.input-portal.asset-characteristics',
                component: () => import('@/vue/pages/asset/input-portal-pages/AssetCharacteristicsPage.vue'),
                meta: {
                    title: 'asset.input-portal.asset-characteristics',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.overview',
                },
            },
            {
                path: 'reporting-data',
                name: 'asset.input-portal.reporting-data',
                component: () => import('@/vue/pages/asset/input-portal-pages/ReportingDataPage.vue'),
                meta: {
                    title: 'asset.input-portal.reporting-data',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.overview',
                },
            },
            {
                path: 'certifications',
                name: 'asset.input-portal.certifications',
                component: () => import('@/vue/pages/asset/input-portal-pages/CertificationsPage.vue'),
                meta: {
                    title: 'general.input-page.certifications',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.overview',
                },
            },
            {
                path: 'meters-and-consumptions',
                name: 'asset.input-portal.meters-and-consumptions',
                component: () => import('@/vue/pages/asset/input-portal-pages/MetersAndConsumptionsPage.vue'),
                meta: {
                    title: 'asset.input-portal.meters-and-consumptions-sign',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.overview',
                },
            },
            {
                path: 'assessments-and-measures',
                name: 'asset.input-portal.assessments-and-measures',
                component: () => import('@/vue/pages/asset/input-portal-pages/RiskAssessmentsPage.vue'),
                meta: {
                    title: 'asset.input-portal.assessments-and-measures',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.overview',
                },
            },
            {
                path: 'roadmap',
                name: 'asset.input-portal.roadmap',
                component: () => import('@/vue/pages/roadmap/asset/AssetRoadmapPage.vue'),
                meta: {
                    title: 'general.measures',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.roadmap',
                },
            },
            {
                path: 'physical-climate',
                name: 'asset.input-portal.physical-climate',
                component: () => import('@/vue/pages/asset/input-portal-pages/PhysicalClimatePage.vue'),
                meta: {
                    title: 'asset.input-portal.physical-climate',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.overview',
                },
            },
            {
                path: 'social-data',
                name: 'asset.input-portal.social-data',
                component: () => import('@/vue/pages/asset/input-portal-pages/SocialDataPage.vue'),
                meta: {
                    title: 'asset.input-portal.social-data',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.overview',
                },
            },
            {
                path: 'financial-data',
                name: 'asset.input-portal.financial-data',
                component: () => import('@/vue/pages/asset/input-portal-pages/FinancialDataPage.vue'),
                meta: {
                    title: 'asset.input-portal.financial-data',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.overview',
                },
            },
            {
                path: 'building-units',
                name: 'asset.input-portal.building-units',
                component: () => import('@/vue/pages/asset/input-portal-pages/BuildingUnitsPage.vue'),
                meta: {
                    title: 'asset.input-portal.building-units',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: (canAccessFeature) => {
                        if (canAccessFeature('building-units-analytics-page')) {
                            return 'asset.building-units'
                        }
                        return 'asset.overview'
                    },
                },
            },
            {
                path: 'development',
                name: 'asset.input-portal.development',
                component: () => import('@/vue/pages/asset/input-portal-pages/DevelopmentPage.vue'),
                meta: {
                    title: 'general.input-page.development',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.overview',
                },
            },
            {
                path: 'document-library',
                name: 'asset.input-portal.document-library',
                component: () => import('@/vue/pages/asset/input-portal-pages/DocumentLibraryPage.vue'),
                meta: {
                    title: 'asset.input-portal.document-library',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.overview',
                },
            },
            {
                path: 'automations',
                name: 'asset.input-portal.automations',
                component: () => import('@/vue/pages/asset/input-portal-pages/AutomationsPage.vue'),
                meta: {
                    title: 'general.automations',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.overview',
                },
            },
            {
                path: 'roadmap-data-collection',
                name: 'asset.roadmap-data-collection',
                component: () => import('@/vue/pages/roadmap/asset/AssetRoadmapPage.vue'),
                meta: {
                    title: 'general.sub-navigation.roadmap-analysis',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.roadmap',
                    feature: {
                        flag: 'input-asset-roadmap-page',
                        fallbackRoute: 'asset.input-portal.overview',
                    },
                },
                beforeEnter: check([PERMISSION_READ_ASSETS], 'asset.input-portal.overview', 'input-asset-roadmap-page'),
            },
            {
                path: 'custom-fields',
                name: 'asset.input-portal.custom-fields',
                component: () => import('@/vue/pages/asset/input-portal-pages/CustomFieldsPage.vue'),
                meta: {
                    title: 'general.input-page.custom-fields',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.overview',
                },
            },
            {
                path: 'audit-log',
                name: 'asset.input-portal.audit-log',
                component: () => import('@/vue/pages/asset/AuditLogPage.vue'),
                meta: {
                    title: 'audit-log.title',
                    showIncompleteAssetsInBreadcrumbs: true,
                    redirectsTo: 'asset.audit-log',
                    feature: {
                        flag: 'new-audit-system',
                        fallbackRoute: 'asset.input-portal.overview',
                    },
                },
            },
        ],
    },
]
